import { SlideContentAnalyzer } from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import {
    ISlideRatingService,
} from '@src/slide/application/service/slide-rating-and-improve/slide-rating-service.interface';
import React from 'react';
import {
    ISlideContentEnumeratorService,
} from '@src/slide/application/service/slide-content-enumerator/slide-content-enumerator-service.interface';
import {
    ISlideContentLayoutAnalyzerService,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/slide-content-layout-analyzer-service.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import {
    IQuickToolDialogManager,
} from '@src/slide/application/service/quick-tool-dialog-manager/core/quick-tool-dialog-manager.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IRightSideBarService } from '../right-side-bar-service/core/interface';
import {
    ISlideContentPreviewService,
} from '@src/slide/application/service/slide-content-preview/core/slide-content-preview.interface';
import {
    IDrawerStateManager,
} from '@src/slide/application/service/drawer-state-manager/core/drawer-state-manager.interface';
import { DrawerService, InjectorService } from '@tencent/workbench';
import { DrawerName } from '@framework/workbench/drawer/constants';
import {
    IRightSideBarManager,
} from '@src/slide/application/service/right-side-bar-manager/right-side-bar-manager.interface';
import { StartView } from '@src/slide/application/service/slide-rating-and-improve/view/pc/start/start';
import { reaction } from 'mobx';
import { AutoShowEntryStrategy } from '@src/slide/application/service/slide-rating-and-improve/entry-strategy/auto-show';
import SwitchManager from '@src/slide/infrastructure/module/dependent/config/switch-system';
import { SWITCH_KEY } from '@src/slide/infrastructure/module/dependent/config/switch-system/contant';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import ua from '@tencent/docs-user-agent';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import i18n from '@i18next/i18n';
import {
    IShowContentChangeOption,
} from '@src/slide/application/service/slide-rating-and-improve/content-layout/result/title-alignment-result';

export class SlideRatingService implements ISlideRatingService {
    private analyzer: SlideContentAnalyzer;
    private selectionService: ISelectionService;
    private drawerService: DrawerService;
    private autoShowStrategy: AutoShowEntryStrategy;

    public constructor(
        uiDataService: IUiDataService,
        selectionService: ISelectionService,
        slideContentEnumerator: ISlideContentEnumeratorService,
        contentLayoutAnalyzer: ISlideContentLayoutAnalyzerService,
        private quickTookDialogManager: IDynamicService<IQuickToolDialogManager>,
        private rightSideBarService: IRightSideBarService,
        private slideContentPreviewService: IDynamicService<ISlideContentPreviewService>,
        private drawerStateManager: IDynamicService<IDrawerStateManager>,
        private rightSideBarManager: IRightSideBarManager,
    ) {
        this.selectionService = selectionService;
        this.rightSideBarService = rightSideBarService;
        this.analyzer = new SlideContentAnalyzer(
            { forbidFontUseNumber: false },
            uiDataService, slideContentEnumerator,
            contentLayoutAnalyzer, this.showUniformFontUI, this.showContentChangeUI,
        );
        this.drawerService = InjectorService.get(DrawerService);
        this.autoShowStrategy = new AutoShowEntryStrategy();

        reaction(
            () => this.rightSideBarManager.isSlideRatingShown,
            (isSlideRatingShown) => {
                if (isSlideRatingShown) {
                    this.rightSideBarService.hide();
                }
            }, { fireImmediately: true },
        );
    }

    public async autoShow() {
        if (!SwitchManager.getKeyValueFromMap(SWITCH_KEY.SLIDE_AUTO_LAYOUT_CHECK_ENTRY)) {
            return;
        }
        // 桌面端本地编辑先不弹出
        if (ENV.IS_LOCAL_EDIT) {
            return;
        }
        const shouldAutoShow = await this.autoShowStrategy.checkIfAutoShow();
        if (shouldAutoShow) {
            this.showDrawer(true);
            this.autoShowStrategy.recordShow();
        }
    }

    public showDrawer(isAuto = false) {
        this.rightSideBarManager.isSlideRatingShown = true;
        this.drawerService.showRightDrawer({
            name: DrawerName.SLIDE_RATING_DRAWER,
            title: undefined,
            content: <StartView
                onCloseClick={() => {
                    this.close();
                }}
                analyzer={this.analyzer}
                selectionService={this.selectionService}
            />,
            onClose: this.close,
            showCloseButton: false,
        });
        this.drawerStateManager.promise().then((manager) => {
            manager.handleDrawerHolderVisiable(true);
            window.dispatchEvent(new Event('resize'));
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.AUTO_BEAUTIFY,
            action: TDWActionType.BEAUTIFY_FUNCTION_VIEW,
            ver5: isAuto ? 1 : 0,
        });
    }

    public hideDrawer() {

    }

    private onEntryClick() {
        this.showDrawer();
    }

    private close = () => {
        this.drawerStateManager.promise().then((manager) => {
            this.rightSideBarManager.isSlideRatingShown = false;
            manager.closeRightDrawer(DrawerName.SLIDE_RATING_DRAWER);
            window.dispatchEvent(new Event('resize'));
        });
        this.analyzer.reset();
    };

    private showUniformFontUI = (onHandled: () => void, onCancel: () => void) => {
        const defaultFont = ua.isMac ? 'PingFang SC' : 'Microsoft YaHei';
        const fontFamilyInfo = {
            titleFontEa: defaultFont,
            titleFontLatin: defaultFont,
            contentFontEa: defaultFont,
            contentFontLatin: defaultFont,
        };
        this.quickTookDialogManager.promise().then((service) => {
            service.showUniformFontDialog(undefined, onHandled, onCancel, fontFamilyInfo);
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.AUTO_BEAUTIFY,
            action: TDWActionType.BEAUTIFY_FONT_QUANTITY,
        });
    };

    private showContentChangeUI = (options: IShowContentChangeOption) => {
        this.slideContentPreviewService.promise().then((previewService) => {
            previewService.show({
                title: i18n.t('标题对齐预览'),
                originList: options.originSlideList,
                previewList: options.toChangeSlideList,
                slideIndexArray: options.toChangeSlideIndexArray,
                slideWidth: options.slideWidth,
                slideHeight: options.slideHeight,
                previewRects: options.toChangeElementRects,
                onConfirmButtonClick: options.onConfirmModify,
                onCancel: options.onCancel,
            });
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.AUTO_BEAUTIFY,
            action: TDWActionType.BEAUTIFY_TITLE_ALIGNMENT_VIEW,
        });
    };
}
